import React, { useState, useEffect, useCallback, memo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";
import { getToken } from "../../utils/auth";
import { useNavigate } from "react-router-dom";


const ContactInfoAdmin = memo(() => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language?.startsWith('ar');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [entries, setEntries] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        email: "",
        phone1: "",
        phone2: "",
        whatsapp: "",
        address_en: "",
        address_ar: "",
        working_hours_en: "",
        working_hours_ar: "",
        facebook: "",
        instagram: "",
        tiktok: "",
        facebook_pixel_id: "",
        tiktok_pixel_id: "",
        location_title_en: "Find Our Showroom",
        location_title_ar: "موقعنا",
        contact_title_en: "Contact Us",
        contact_title_ar: "تواصل معنا",
        business_hours_title_en: "Business Hours",
        business_hours_title_ar: "ساعات العمل",
        business_hours_desc_en: "",
        business_hours_desc_ar: "",
        rapid_response_title_en: "Rapid Response",
        rapid_response_title_ar: "استجابة سريعة",
        rapid_response_desc_en: "",
        rapid_response_desc_ar: ""
    });

    const fetchEntries = useCallback(async () => {
        setLoading(true);
        try {
            const token = getToken();
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/contact-info`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEntries(res.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const handleEdit = (entry) => {
        setEditingId(entry.id);
        setForm(entry);
        setShowModal(true);
    };

    const handleAdd = () => {
        setEditingId(null);
        setForm({
            email: "", phone1: "", phone2: "", whatsapp: "",
            address_en: "", address_ar: "",
            working_hours_en: "", working_hours_ar: "",
            facebook: "", instagram: "", tiktok: "",
            facebook_pixel_id: "", tiktok_pixel_id: "",
            location_title_en: "Find Our Showroom", location_title_ar: "موقعنا",
            contact_title_en: "Contact Us", contact_title_ar: "تواصل معنا",
            business_hours_title_en: "Business Hours", business_hours_title_ar: "ساعات العمل",
            business_hours_desc_en: "", business_hours_desc_ar: "",
            rapid_response_title_en: "Rapid Response", rapid_response_title_ar: "استجابة سريعة",
            rapid_response_desc_en: "", rapid_response_desc_ar: ""
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('admin.contact_info.delete_confirm'))) return;
        try {
            const token = getToken();
            await axios.delete(`${CONFIG.API_URL}/api/admin/contact-info/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchEntries();
        } catch (err) {
            alert(t('admin.contact_info.delete_failed'));
        }
    };

    const handleToggleActive = async (id) => {
        try {
            const token = getToken();
            await axios.patch(`${CONFIG.API_URL}/api/admin/contact-info/${id}/active`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchEntries();
        } catch (err) {
            alert(t('admin.contact_info.activate_failed'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const token = getToken();
            if (editingId) {
                await axios.put(`${CONFIG.API_URL}/api/admin/contact-info/${editingId}`, form, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post(`${CONFIG.API_URL}/api/admin/contact-info`, form, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            setShowModal(false);
            fetchEntries();
        } catch (err) {
            alert(t('admin.contact_info.save_failed'));
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const [search, setSearch] = useState("");

    const filteredEntries = entries.filter(e =>
        (e.email && e.email.toLowerCase().includes(search.toLowerCase())) ||
        (e.phone1 && e.phone1.toLowerCase().includes(search.toLowerCase())) ||
        (e.phone2 && e.phone2.toLowerCase().includes(search.toLowerCase())) ||
        e.id.toString().includes(search)
    );

    return (
        <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            <div className="flex flex-wrap justify-between items-center gap-4 md:gap-0 mb-12">
                <div>
                    <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t('admin.contact_info.title')}</h2>
                    <p className="text-gray-500 font-medium mt-2">{t('admin.contact_info.subtitle')}</p>
                </div>
                <div className="flex gap-4">
                    <button onClick={handleAdd} className="bg-brand-primary text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-xl shadow-brand-primary/20">
                        <i className="fas fa-plus mr-2"></i> {t('admin.contact_info.add_btn')}
                    </button>
                    <button onClick={() => navigate("/admin")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                        <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>
            <div className="mb-8 flex flex-sm-wrap flex-md-auto items-center bg-gray-50 p-4 rounded-[30px] border border-gray-100">
                <div className="relative w-full">
                    <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('admin.contact_info.search_placeholder')}
                        className="pl-12 pr-4 py-3 rounded-2xl border-none bg-gray-50 focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium w-64"
                    />
                </div>
            </div>

            {loading ? (
                <div className="text-center py-20"><i className="fas fa-spinner fa-spin text-4xl text-brand-primary"></i></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredEntries.map(entry => (
                        <div key={entry.id} className={`p-8 rounded-[40px] border transition-all ${entry.is_active ? 'border-brand-primary bg-brand-primary/5 shadow-xl shadow-brand-primary/10' : 'border-gray-100 bg-white shadow-sm hover:shadow-lg'}`}>
                            <div className="flex justify-between items-start mb-6 sm:flex-wrap md:flex-auto">
                                <div className="flex items-center gap-3">
                                    <div className="relative group">
                                        <span
                                            className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-gray-900 text-white md:hidden inline-block cursor-pointer"
                                            onClick={() => navigator.clipboard.writeText(entry.id.toString())}
                                        >
                                            #{entry.id}
                                        </span>
                                        <span
                                            className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-gray-900 text-white hidden md:inline-block cursor-pointer"
                                            onClick={() => navigator.clipboard.writeText(entry.id.toString())}
                                        >
                                            #{entry.id.toString().slice(0, 5)}{entry.id.toString().length > 5 ? '...' : ''}
                                        </span>
                                        {entry.id.toString().length > 5 && (
                                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-lg shadow-lg z-10 whitespace-nowrap">
                                                #{entry.id}
                                            </div>
                                        )}
                                    </div>
                                    <div className={`w-3 h-3 rounded-full ${entry.is_active ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div>
                                    <span className={`text-xs font-black uppercase tracking-widest ${entry.is_active ? 'text-brand-primary' : 'text-gray-400'}`}>
                                        {entry.is_active ? t('admin.contact_info.active') : t('admin.contact_info.inactive')}
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleToggleActive(entry.id)} className={`w-8 h-8 rounded-xl transition-all ${entry.is_active ? 'bg-orange-50 text-orange-600 hover:bg-orange-600' : 'bg-green-50 text-green-600 hover:bg-green-600'} hover:text-white`} title={entry.is_active ? t('admin.contact_info.deactivate', 'Deactivate') : t('admin.contact_info.activate', 'Activate')}>
                                        <i className={`fas ${entry.is_active ? 'fa-times' : 'fa-check'}`}></i>
                                    </button>
                                    <button onClick={() => handleEdit(entry)} className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all" title={t('admin.common.edit', 'Edit')}>
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button onClick={() => handleDelete(entry.id)} className="w-8 h-8 rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-all" title={t('admin.common.delete', 'Delete')}>
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.status')}</span>
                                    <span className={`text-[10px] font-black uppercase tracking-widest ${entry.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                                        {entry.is_active ? t('admin.contact_info.active') : t('admin.contact_info.inactive')}
                                    </span>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">{t('admin.contact_info.email')}</p>
                                    <p className="text-sm font-black text-gray-900 truncate">{entry.email}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">{t('admin.contact_info.phone1')}</p>
                                    <p className="text-sm font-black text-gray-900">{entry.phone1}</p>
                                </div>
                                {entry.phone2 && (
                                    <div className="space-y-1">
                                        <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">{t('admin.contact_info.phone2')}</p>
                                        <p className="text-sm left-3 font-black text-gray-900">{entry.phone2}</p>
                                    </div>
                                )}
                                <div className="flex gap-2 pt-2">
                                    {entry.whatsapp && <i className="fab fa-whatsapp text-green-600"></i>}
                                </div>
                                {(entry.facebook_pixel_id || entry.tiktok_pixel_id) && (
                                    <div className="pt-2 border-t border-gray-50 mt-2 space-y-2">
                                        {entry.facebook_pixel_id && (
                                            <div className="flex justify-between items-center bg-blue-50/30 p-2 rounded-xl">
                                                <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">{t('admin.contact_info.fb_pixel', 'FB Pixel')}</span>
                                                <span className="text-[10px] font-bold text-gray-700">{entry.facebook_pixel_id}</span>
                                            </div>
                                        )}
                                        {entry.tiktok_pixel_id && (
                                            <div className="flex justify-between items-center bg-gray-50 p-2 rounded-xl">
                                                <span className="text-[9px] font-black text-gray-900 uppercase tracking-widest">{t('admin.contact_info.tt_pixel', 'TT Pixel')}</span>
                                                <span className="text-[10px] font-bold text-gray-700">{entry.tiktok_pixel_id}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="space-y-1">
                                    <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">{t('admin.contact_info.address')}</p>
                                    <p className="text-xs font-medium text-gray-600 line-clamp-2">{entry.address_en}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                    {entries.length === 0 && (
                        <div className="col-span-full py-20 bg-gray-50 rounded-[40px] text-center border-2 border-dashed border-gray-200">
                            <p className="text-gray-400 font-black uppercase tracking-widest text-xs">{t('admin.common.no_items', 'No items found')}</p>
                        </div>
                    )}
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto pt-24 pb-12">
                    <div className="bg-white rounded-[50px] w-full max-w-4xl p-10 shadow-2xl animate-scale-in">
                        <div className="flex justify-between items-center mb-10">
                            <h3 className="text-3xl font-black text-gray-900 tracking-tight">{editingId ? t('admin.contact_info.edit') : t('admin.contact_info.create')}</h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-red-500 transition-colors">
                                <i className="fas fa-times text-2xl"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t('admin.contact_info.email')}</label>
                                    <input name="email" value={form.email} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold" required />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t('admin.contact_info.phone1')}</label>
                                    <input name="phone1" value={form.phone1} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold" required />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t('admin.contact_info.phone2')}</label>
                                    <input name="phone2" value={form.phone2} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t('admin.contact_info.whatsapp')}</label>
                                    <input name="whatsapp" value={form.whatsapp} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold" required />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t('admin.contact_info.facebook')}</label>
                                    <input name="facebook" value={form.facebook} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold" placeholder="https://facebook.com/..." />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t('admin.contact_info.instagram')}</label>
                                    <input name="instagram" value={form.instagram} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold" placeholder="https://instagram.com/..." />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t('admin.contact_info.tiktok')}</label>
                                    <input name="tiktok" value={form.tiktok} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold" placeholder="https://tiktok.com/@..." />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t('admin.contact_info.facebook_pixel_id')}</label>
                                    <input name="facebook_pixel_id" value={form.facebook_pixel_id} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold" placeholder="Pixel ID" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t('admin.contact_info.tiktok_pixel_id')}</label>
                                    <input name="tiktok_pixel_id" value={form.tiktok_pixel_id} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold" placeholder="Pixel ID" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4 border-l-4 border-brand-primary/20 pl-6">
                                    <h4 className="text-xs font-black uppercase tracking-widest text-brand-primary">{t('admin.contact_info.en_content')}</h4>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.location_title')}</label>
                                        <input name="location_title_en" value={form.location_title_en} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.contact_title')}</label>
                                        <input name="contact_title_en" value={form.contact_title_en} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.address')}</label>
                                        <textarea name="address_en" value={form.address_en} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold h-20" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.business_hours_title')}</label>
                                        <input name="business_hours_title_en" value={form.business_hours_title_en} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.working_hours_summary')}</label>
                                        <input name="working_hours_en" value={form.working_hours_en} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.business_hours_desc')}</label>
                                        <textarea name="business_hours_desc_en" value={form.business_hours_desc_en} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold h-20" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.rapid_response_title')}</label>
                                        <input name="rapid_response_title_en" value={form.rapid_response_title_en} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.rapid_response_desc')}</label>
                                        <textarea name="rapid_response_desc_en" value={form.rapid_response_desc_en} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold h-20" />
                                    </div>
                                </div>

                                <div className="space-y-4 border-r-4 border-brand-primary/20 pr-6 text-right" dir="rtl">
                                    <h4 className="text-xs font-black uppercase tracking-widest text-brand-primary">{t('admin.contact_info.ar_content')}</h4>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.location_title')}</label>
                                        <input name="location_title_ar" value={form.location_title_ar} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.contact_title')}</label>
                                        <input name="contact_title_ar" value={form.contact_title_ar} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.address')}</label>
                                        <textarea name="address_ar" value={form.address_ar} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold h-20" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.business_hours_title')}</label>
                                        <input name="business_hours_title_ar" value={form.business_hours_title_ar} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.working_hours_summary')}</label>
                                        <input name="working_hours_ar" value={form.working_hours_ar} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.business_hours_desc')}</label>
                                        <textarea name="business_hours_desc_ar" value={form.business_hours_desc_ar} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold h-20" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.rapid_response_title')}</label>
                                        <input name="rapid_response_title_ar" value={form.rapid_response_title_ar} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('admin.contact_info.rapid_response_desc')}</label>
                                        <textarea name="rapid_response_desc_ar" value={form.rapid_response_desc_ar} onChange={handleChange} className="bg-gray-50 border-none p-4 w-full rounded-2xl font-bold h-20" />
                                    </div>
                                </div>
                            </div>

                            <button type="submit" disabled={saving} className="bg-brand-primary text-white py-5 px-12 rounded-2xl w-full font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2">
                                {saving && <i className="fas fa-spinner fa-spin"></i>}
                                {saving ? t("admin.common.saving", "Saving...") : (editingId ? t('admin.contact_info.update_btn') : t('admin.contact_info.save_btn'))}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
});

export default ContactInfoAdmin;
